/** @shell standard @header bar */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Alert, Platform, Image, ScrollView, KeyboardAvoidingView } from 'react-native';
import Animated from 'react-native-reanimated';
import { showActionSheet } from '../components/ui/showActionSheet';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { SCROLL_VIEW_PROPS } from '../shared/utils/scrollListPerf';
import { SvgIcon } from '../components/common/SvgIcon';
import { MainLoader } from '../components/load-main/MainLoader';
import { Input } from '../components/common/Input';
import { Clickable } from '../components/common/Clickable';
import { Clickable2 } from '../components/common/Clickable2';
import { SolidCtaClickable } from '../components/common/SolidCtaClickable';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import { useRoute, useFocusEffect } from '@react-navigation/native';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import moment from 'moment';
import { formatMomentForApiYmd } from '../shared/utils/dateUtils';
import { validateCoupon } from '../data/domains/orders';
import { useAuthStore } from '../shared/auth/store';
import { useSubscriptionContext, usePendingSubscriptions } from '../data/domains/subscription';
import {
  computeCheckoutMinStartYmd,
  findFirstSelectableCheckoutStartYmd,
  isCheckoutStartDateSelectable as isCheckoutStartDateSelectableHelper,
  isDateWithinCheckoutBlockedRanges,
  getSelectedSubscription,
  setSelectedSubscription,
  clearSelectedSubscription,
  getPlanDisplayPrice,
  hasAdminNoMealsOnDays,
  initV3CheckoutDayOffFromPlan,
  type CheckoutStartDateRules,
} from '../data/domains/subscription';
import { useAddresses, useSetDefaultAddress } from '../data/domains/addresses';
import { useReferrals } from '../data/domains/loyalty';
import {
  CheckoutGlobalService,
  CheckoutContextValue,
  PendingCheckoutData,
  formatCheckoutDayOffSummary,
  buildSubscriptionCheckoutRequest,
  postSubscriptionCheckoutRequest,
  isUsableCheckoutPlan,
  resolveCheckoutPlanIds,
  resolveSubscriptionCheckoutPaymentUrl,
  invalidateOnCheckoutSuccess,
  type SubscriptionCheckoutMode,
} from '../data/domains/checkout';
import { useApiConfig, useLoyaltyFeatureEnabled, useOrganizationSettings, resolveOrganizationPaymentMode } from '../data/domains/config';
import { useTranslation, getApiText, getLocalizedWeekdaysShort, formatNumber } from '../shared/i18n';
import { useRequireVerified } from '../shared/auth/useRequireVerified';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { CheckoutStartDateCalendarSheet } from './components/CheckoutStartDateCalendarSheet';
import { CheckoutAddressPickerSheet } from './components/CheckoutAddressPickerSheet';
import { formatAddressSummaryLine } from '../data/domains/addresses';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useQueryClient } from '@tanstack/react-query';
import { horizontalLtrDirectionStyle } from '../shared/i18n/rtl';
import { pillButtonLabelCentered } from '../shared/theme/sharedStyles';
import { resolveImageUri } from '../shared/utils/resolveImageUri';
import { formatCurrency, getDefaultCurrencyCode } from '../shared/utils/currencyUtils';
import { pickCmsPageImageUri } from '../shared/utils/resolveCmsImageUri';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  StackScreenShell,
  DASH_STUDIO_RADIUS,
  studioCardShell,
  androidScaleFont,
  androidScaleSize,
  STACK_SCROLL_GUTTER,
} from '../shared/theme/studio/stackScreenShell';
import { DASH_STUDIO_INNER } from '../shared/theme/studio/dashboardConstants';
import { androidResolveShadow, androidFlatSurfaceStyle } from '../shared/theme/androidUiParity';
import {
  CheckoutSectionBlock,
  CheckoutStudioCard,
  CheckoutCollapsibleSection,
  CheckoutInCardDivider,
  CheckoutNavRow,
  CheckoutPlanHeroCard,
  CheckoutSummaryRow,
  useCheckoutCollapse,
} from './components/CheckoutStudioLayout';
import {
  CHECKOUT_CHIP_RADIUS,
  CHECKOUT_FOOTER_VERTICAL_PAD,
  checkoutFooterScrollPadding,
} from './components/checkoutLayout';
/** Single horizontal gutter for checkout content (titles + cards align to one column). */
const CHECKOUT_GAP_BELOW_HEADER = appTheme.spacing.sm;

const HESABE_PAYMENT_TYPES = [
  { id: 1, name: 'KNET' },
  { id: 9, name: 'Apple Pay' },
  { id: 2, name: 'Visa/MasterCard' },
] as const;

/** Referral API may send `reward_value` as number or string (with optional `%`). */
function referralRewardIsPercentage(reward_value: unknown): boolean {
  return String(reward_value ?? '').includes('%');
}

function parseReferralRewardAmount(reward_value: unknown): number {
  const normalized = String(reward_value ?? '').replace(/%/g, '').trim();
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

export const CheckoutScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const route = useRoute();
  const { t, locale } = useTranslation();
  const user = useAuthStore(s => s.user);

  useRequireVerified('Checkout');
  const query_client = useQueryClient();
  const { organization, refreshConfig } = useApiConfig();
  const show_loyalty = useLoyaltyFeatureEnabled();
  const { settings: date_settings } = useOrganizationSettings();
  const package_start_after = Number(date_settings.package_start_after ?? 0);
  const week_day_off = Number(date_settings.week_day_off ?? 0);
  const { activeDateRanges, maxDateForCheckout, isLoading: subscription_dates_loading, refetch: refetchSubscriptionDates } =
    useSubscriptionContext();
  const { pendingPlan, cancelPending } = usePendingSubscriptions();
  const insets = useSafeAreaInsets();
  const header_top = useHeaderTopPadding();
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const scroll_ref = useRef<Animated.ScrollView>(null);
  const coupon_section_y_ref = useRef(0);
  const { data: addressesData, isLoading: addresses_loading, refetch: refetchAddresses } = useAddresses();
  const addresses = useMemo(() => (Array.isArray(addressesData) ? addressesData : []), [addressesData]);
  const { data: referralsData = [] } = useReferrals();
  const setDefaultAddressMutation = useSetDefaultAddress();

  const defaultPlan = useMemo(
    () => ({
      id: 0,
      name: 'Premium Plan',
      total_meals: 3,
      total_snacks: 2,
      days: 30,
      cost: 150,
      offer_cost: 140,
      currency: getDefaultCurrencyCode(),
      max_day_off: 1,
      no_meals_on_days: [],
      addons: undefined,
    }),
    [],
  );

  const [planFromStorage, setPlanFromStorage] = useState<any>(null);
  const route_param_plan = (route.params as { plan?: unknown; data?: unknown } | undefined)?.plan ?? (route.params as { data?: unknown } | undefined)?.data;

  /** Keep AsyncStorage in sync with route params *before* other effects (e.g. unverified → OTP) run. */
  useLayoutEffect(() => {
    if (route_param_plan && typeof route_param_plan === 'object') {
      setPlanFromStorage(route_param_plan);
      void setSelectedSubscription(route_param_plan);
    }
  }, [route_param_plan]);

  useEffect(() => {
    if (route_param_plan && typeof route_param_plan === 'object') {
      return;
    }
    let cancelled = false;
    getSelectedSubscription().then(stored => {
      if (cancelled) {
        return;
      }
      if (stored && isUsableCheckoutPlan(stored)) {
        setPlanFromStorage(stored);
        /** Intentionally do not clear here: recovery after `navigation.reset({ name: 'Checkout' })` without params. */
      }
    });
    return () => {
      cancelled = true;
    };
  }, [route_param_plan]);

  const plan = useMemo(() => {
    const candidates = [route_param_plan, planFromStorage, defaultPlan];
    for (const candidate of candidates) {
      if (isUsableCheckoutPlan(candidate)) {
        return candidate;
      }
    }
    return defaultPlan;
  }, [route_param_plan, planFromStorage, defaultPlan]);

  const plan_record = useMemo(() => plan as Record<string, unknown>, [plan]);

  const display_currency = useMemo((): string => getDefaultCurrencyCode(), [locale]);
  const free_days_selected = (route.params as any)?.free_days_selected ?? (plan as any)?.free_days_selected ?? 0;
  const base_days = Number((plan as any)?.days ?? (plan as any)?.duration ?? (plan as any)?.type_key ?? (plan as any)?.day ?? 0) || 0;
  const planDays = base_days + free_days_selected;

  const v3_uses_admin_day_offs = useMemo(() => hasAdminNoMealsOnDays(plan_record), [plan_record]);

  const checkout_date_rules_base = useMemo((): CheckoutStartDateRules => {
    const max_fallback = moment().locale('en').add(2, 'years').format('YYYY-MM-DD');
    return {
      minStartYmd: computeCheckoutMinStartYmd(package_start_after, activeDateRanges),
      maxStartYmd: maxDateForCheckout || max_fallback,
      /** Ignore org `week_day_off` when admin configured `no_meals_on_days` on the plan. */
      weekDayOff: v3_uses_admin_day_offs ? 0 : week_day_off,
      activeDateRanges,
    };
  }, [package_start_after, week_day_off, activeDateRanges, maxDateForCheckout, v3_uses_admin_day_offs]);

  const min_checkout_start_date = useMemo(
    () => moment(checkout_date_rules_base.minStartYmd, 'YYYY-MM-DD', true).startOf('day'),
    [checkout_date_rules_base.minStartYmd],
  );

  const max_checkout_month = useMemo(
    () => moment(checkout_date_rules_base.maxStartYmd, 'YYYY-MM-DD', true).startOf('month'),
    [checkout_date_rules_base.maxStartYmd],
  );

  const isDateBlockedByActiveSubscription = useCallback(
    (date_str: string) => isDateWithinCheckoutBlockedRanges(date_str, activeDateRanges),
    [activeDateRanges],
  );

  const [startDate, setStartDate] = useState(moment());
  const [selectedAddress, setSelectedAddress] = useState<any>(null);

  /** Resolves a delivery address for subscription & payment; never call subscription APIs without an id. */
  const address_for_subscription = useMemo(() => {
    if (selectedAddress && selectedAddress.id != null) {
      return selectedAddress;
    }
    if (addresses.length > 0) {
      return addresses[0] as any;
    }
    return null;
  }, [selectedAddress, addresses]);

  const has_saved_delivery_address = useMemo(
    () => address_for_subscription != null && address_for_subscription.id != null && String(address_for_subscription.id) !== '',
    [address_for_subscription],
  );

  const [selectedPayment, setSelectedPayment] = useState('');
  const [paymentType, setPaymentType] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [usePoints, setUsePoints] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showAddressPicker, setShowAddressPicker] = useState(false);
  const [showAddonsPopup, setShowAddonsPopup] = useState(false);
  const [dayOffArray, setDayOffArray] = useState<number[]>([]);
  const [tempDayOffArray, setTempDayOffArray] = useState<any[]>([]);
  const [forcedDayOff, setForcedDayOff] = useState<number[]>([]);
  const [maxDayOff, setMaxDayOff] = useState(1);
  const [addons, setAddons] = useState<any[]>([]);
  const selectedAddons = useMemo(() => addons.filter((a: any) => a.selectedQty > 0), [addons]);
  const coupon_collapse = useCheckoutCollapse(false, couponApplied);
  const open_addons_popup = useCallback(() => {
    setShowAddonsPopup(true);
  }, []);
  const adjustAddonQty = (addon_id: number | string, delta: number) => {
    setAddons(prev =>
      prev.map((a: any) => {
        if (a.id !== addon_id) return a;
        const max_qty = a.max_quantity ?? 99;
        const next_qty = Math.max(0, Math.min(max_qty, (a.selectedQty || 0) + delta));
        return { ...a, selectedQty: next_qty };
      }),
    );
  };
  const [selectedReferrals, setSelectedReferrals] = useState<any[]>([]);
  const referrals = Array.isArray(referralsData) ? referralsData : [];
  const [_checkoutContext, setCheckoutContext] = useState<CheckoutContextValue | null>(null);
  const did_init_start_date_ref = useRef(false);

  const checkout_date_rules = useMemo(
    (): CheckoutStartDateRules => ({
      ...checkout_date_rules_base,
      userDayOffs: dayOffArray,
    }),
    [checkout_date_rules_base, dayOffArray],
  );

  const default_checkout_start_ymd = useMemo(
    () => findFirstSelectableCheckoutStartYmd(checkout_date_rules.minStartYmd, checkout_date_rules),
    [checkout_date_rules],
  );

  const can_complete_subscription_purchase =
    has_saved_delivery_address && !addresses_loading && !subscription_dates_loading && default_checkout_start_ymd != null;

  useEffect(() => {
    if (subscription_dates_loading || !default_checkout_start_ymd) {
      return;
    }
    const context = CheckoutGlobalService.getContext();
    if (context?.type === 'pending') {
      return;
    }
    setStartDate(prev => {
      const prev_ymd = formatMomentForApiYmd(prev);
      if (!did_init_start_date_ref.current) {
        did_init_start_date_ref.current = true;
        return moment(default_checkout_start_ymd, 'YYYY-MM-DD', true).startOf('day');
      }
      if (isCheckoutStartDateSelectableHelper(prev_ymd, checkout_date_rules)) {
        return prev;
      }
      return moment(default_checkout_start_ymd, 'YYYY-MM-DD', true).startOf('day');
    });
  }, [subscription_dates_loading, default_checkout_start_ymd, checkout_date_rules]);

  useEffect(() => {
    const context = CheckoutGlobalService.getContext();
    if (context) {
      setCheckoutContext(context);
      if (context.type === 'pending') {
        const data = context.data as PendingCheckoutData;
        setStartDate(moment(data.selectedDate));
        setCouponCode(data.couponCode || '');
        if (data.discountAmount > 0) {
          setCouponDiscount(data.discountAmount);
          setCouponApplied(true);
        }
      }
    }

    const unsubscribe = CheckoutGlobalService.subscribeToContext(ctx => {
      setCheckoutContext(ctx);
    });
    return () => unsubscribe();
  }, []);

  const planDayOffKey = useMemo(() => {
    const normalized = initV3CheckoutDayOffFromPlan(plan_record, week_day_off);
    return `${plan_record.id ?? 0}-${normalized.max_day_off}-${JSON.stringify(
      (Array.isArray(plan_record.no_meals_on_days) ? plan_record.no_meals_on_days : []).map((d: any) => ({
        day: d?.day,
        is_forced: d?.is_forced,
      })),
    )}`;
  }, [plan_record, week_day_off]);

  useEffect(() => {
    const { max_day_off, forced_day_offs, temp_day_off_options, day_off_array } = initV3CheckoutDayOffFromPlan(
      plan_record,
      week_day_off,
    );
    setForcedDayOff(forced_day_offs);
    setTempDayOffArray(temp_day_off_options);
    setDayOffArray(day_off_array);
    setMaxDayOff(max_day_off);
  }, [planDayOffKey, plan_record, week_day_off]);

  useEffect(() => {
    if (addresses.length === 0) {
      return;
    }
    const defaultAddr = addresses.find((a: any) => a.is_default) || addresses[0];
    setSelectedAddress((prev: any) => (prev?.id === defaultAddr?.id ? prev : defaultAddr));
  }, [addresses]);

  useFocusEffect(
    useCallback(() => {
      if (user?.id != null) {
        void refetchAddresses();
        void refetchSubscriptionDates();
      }
    }, [user?.id, refetchAddresses, refetchSubscriptionDates]),
  );

  const first_checkout_address_prompt = useRef(false);
  useEffect(() => {
    if (first_checkout_address_prompt.current || addresses_loading) {
      return;
    }
    if (user?.id == null) {
      return;
    }
    if (has_saved_delivery_address) {
      return;
    }
    first_checkout_address_prompt.current = true;
    Alert.alert(t('checkout.addressRequired'), t('checkout.addAddressBeforeSubscription'), [
      { text: t('common.cancel'), style: 'cancel' },
      { text: t('address.addAddress'), onPress: () => (navigation as { navigate: (n: string, p?: object) => void }).navigate('AddressForm', {}) },
    ]);
  }, [addresses_loading, has_saved_delivery_address, user?.id, t, navigation]);

  const plan_addons_record = plan_record;
  const planAddonsRaw =
    plan_addons_record.addons ?? plan_addons_record.add_ons ?? plan_addons_record.subscription_addons;
  const planAddonsArray = Array.isArray(planAddonsRaw) ? planAddonsRaw : [];
  const planAddonsKey = plan_addons_record.id !== undefined ? `${plan_addons_record.id}-${planAddonsArray.length}` : '';
  useEffect(() => {
    if (planAddonsArray.length > 0) {
      setAddons(
        planAddonsArray.map((addon: any) => ({
          ...addon,
          selectedQty: 0,
          max_quantity: addon.max_quantity ?? addon.max_qty ?? addon.maximum_quantity ?? 99,
        })),
      );
    } else {
      setAddons([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- planAddonsArray derived from plan
  }, [planAddonsKey]);

  const addonsTotal = selectedAddons.reduce((sum, addon) => {
    const deliveryCharge = parseFloat(addon.delivery_charge) || 0;
    const qty = parseInt(addon.selectedQty) || 0;
    return sum + deliveryCharge * qty * planDays;
  }, 0);

  const planCost = getPlanDisplayPrice(plan);
  const subtotal = planCost + addonsTotal;

  const referralDiscount = selectedReferrals.reduce((sum, ref) => {
    const value = parseReferralRewardAmount(ref.reward_value);
    if (referralRewardIsPercentage(ref.reward_value)) {
      return sum + (subtotal * value) / 100;
    }
    return sum + value;
  }, 0);

  const loyaltyPoints = parseInt((user as any)?.available_loyalty_points) || 0;
  const singlePointRedeemValue = parseFloat((user as any)?.single_point_redeem_value) || 0.01;
  const minimumInvoiceToRedeem = parseFloat((user as any)?.minimum_invoice_value_to_redeem_loyalty) || 0;
  const pointsValue = loyaltyPoints * singlePointRedeemValue;
  const canRedeemPoints = subtotal >= minimumInvoiceToRedeem && loyaltyPoints > 0;

  const paymentGatewayArray = useMemo(() => organization?.payment_gateway || [], [organization?.payment_gateway]);
  const payment_mode = useMemo(
    () => resolveOrganizationPaymentMode(organization?.payment_mode),
    [organization?.payment_mode],
  );
  const paymentMethods = useMemo(() => {
    if (!Array.isArray(paymentGatewayArray) || paymentGatewayArray.length === 0) {
      return [];
    }

    return paymentGatewayArray.map((gateway: any) => {
      const gatewayName = gateway.name?.toLowerCase() || '';
      let icon = 'card';

      if (gatewayName.includes('visa') || gatewayName.includes('mastercard')) {
        icon = 'card';
      } else if (gatewayName.includes('hesabe')) {
        icon = 'wallet';
      } else if (gatewayName.includes('tabby')) {
        icon = 'wallet';
      }

      return {
        id: gateway.name,
        name: gateway.name,
        displayName: gateway.display_name || gateway.name,
        icon,
      };
    });
  }, [paymentGatewayArray]);

  /** CTA + place-order path from `payment_mode` (gateways still required for online pay). */
  const can_pay_online = payment_mode !== 'manual' && paymentMethods.length > 0;
  const checkout_cta_is_request = !can_pay_online;

  const is_hesabe_gateway = useMemo(
    () => typeof selectedPayment === 'string' && selectedPayment.toLowerCase().includes('hesabe'),
    [selectedPayment],
  );

  const show_payment_gateway_choice = paymentMethods.length > 1;
  /** Hesabe KNET / card / Apple: only show type chips when the org has more than one payment gateway; if there is a single configured gateway, default `paymentType` and hide the chooser. */
  const show_hesabe_payment_type_picker = is_hesabe_gateway && paymentMethods.length > 1;

  useEffect(() => {
    if (paymentMethods.length === 0) return;
    const only_id = paymentMethods.length === 1 ? paymentMethods[0].id : null;
    if (!selectedPayment) {
      setSelectedPayment(paymentMethods[0].id);
      return;
    }
    if (only_id != null && selectedPayment !== only_id) {
      setSelectedPayment(only_id);
    }
  }, [paymentMethods, selectedPayment]);

  const show_payment_section = paymentMethods.length > 0 && (show_payment_gateway_choice || show_hesabe_payment_type_picker);

  const pointsDiscount = usePoints ? Math.min(pointsValue, subtotal - couponDiscount - referralDiscount) : 0;
  const total = Math.max(0, subtotal - couponDiscount - referralDiscount - pointsDiscount);

  const toggleReferral = (referral: any) => {
    const isCurrentlySelected = selectedReferrals.some(r => r.id === referral.id);
    if (isCurrentlySelected) {
      setSelectedReferrals(selectedReferrals.filter(r => r.id !== referral.id));
    } else {
      setSelectedReferrals([...selectedReferrals, referral]);
    }
  };

  const clearOldPending = async () => {
    if (pendingPlan?.id) {
      try {
        await cancelPending(pendingPlan.id);
      } catch {
        // Best-effort cleanup before new checkout.
      }
    }
  };

  const validateCheckoutStartDate = useCallback((): boolean => {
    if (subscription_dates_loading) {
      Alert.alert(t('common.error'), t('checkout.loadingSubscriptionDates'));
      return false;
    }
    const selected_ymd = formatMomentForApiYmd(startDate);
    if (!isCheckoutStartDateSelectableHelper(selected_ymd, checkout_date_rules)) {
      Alert.alert(t('common.error'), t('checkout.invalidStartDate'));
      if (default_checkout_start_ymd) {
        setStartDate(moment(default_checkout_start_ymd, 'YYYY-MM-DD', true).startOf('day'));
      }
      return false;
    }
    return true;
  }, [subscription_dates_loading, startDate, checkout_date_rules, default_checkout_start_ymd, t]);

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      const { subscription_id } = resolveCheckoutPlanIds(plan as Record<string, unknown>);
      if (__DEV__) {
        console.log('[Checkout][v3] applyCoupon → GET', {
          customer_id: user?.id,
          coupon_code: couponCode,
          subscription_id,
        });
      }
      const response: any = await validateCoupon({
        customer_id: user?.id,
        coupon_code: couponCode,
        subscription_id,
      });

      if (response.success && response.data?.coupon) {
        const coupon = response.data.coupon;
        let discount = 0;

        if (coupon.type?.toUpperCase() === 'PERCENTAGE') {
          discount = (subtotal * coupon.value) / 100;
        } else {
          discount = coupon.value;
        }

        setCouponDiscount(discount);
        setCouponApplied(true);
        Alert.alert(t('common.success'), response.message || t('checkout.couponApplied'));
      } else {
        Alert.alert(t('common.error'), response.message || t('checkout.invalidCoupon'));
      }
    } catch (error: any) {
      Alert.alert(t('common.error'), error.message || t('checkout.failedToValidateCoupon'));
    } finally {
      setIsLoading(false);
    }
  };

  const ensureAddressDefaultThenProceed = async () => {
    const addr = address_for_subscription;
    if (!addr || !user?.id) {
      return;
    }
    if (addr.is_default) {
      return;
    }
    try {
      await setDefaultAddressMutation.mutateAsync({
        customerId: user.id,
        addressId: addr.id,
      });
    } catch (_e) {
      // Non-blocking: backend may still accept address_id
    }
  };

  const addressPayload = useMemo(() => {
    const a = address_for_subscription;
    if (a == null || a.id == null || String(a.id) === '') {
      return {};
    }
    return {
      address_id: a.id,
      customer_address_id: a.id,
    };
  }, [address_for_subscription]);

  const go_to_add_address = useCallback(() => {
    (navigation as { navigate: (n: string, p?: object) => void }).navigate('AddressForm', {});
  }, [navigation]);

  const requireDeliveryAddress = useCallback((): boolean => {
    if (addresses_loading) {
      return false;
    }
    if (!has_saved_delivery_address) {
      Alert.alert(t('checkout.addressRequired'), t('checkout.addAddressBeforeSubscription'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('address.addAddress'), onPress: go_to_add_address },
      ]);
      return false;
    }
    if (Object.keys(addressPayload).length === 0) {
      Alert.alert(t('checkout.addressRequired'), t('checkout.addAddressBeforeSubscription'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('address.addAddress'), onPress: go_to_add_address },
      ]);
      return false;
    }
    return true;
  }, [addresses_loading, has_saved_delivery_address, addressPayload, t, go_to_add_address]);

  const submitSubscriptionCheckout = useCallback(
    async (mode: SubscriptionCheckoutMode) => {
      if (!agreedToTerms) {
        Alert.alert(t('common.error'), t('checkout.agreeToTermsRequired'));
        return;
      }
      if (!requireDeliveryAddress()) {
        return;
      }
      if (!validateCheckoutStartDate()) {
        return;
      }
      if (!isUsableCheckoutPlan(plan)) {
        Alert.alert(t('common.error'), t('checkout.missingPlan'));
        return;
      }
      if (typeof user?.id !== 'number') {
        return;
      }
      if (mode === 'pay' && paymentMethods.length > 0 && !selectedPayment) {
        Alert.alert(t('common.error'), t('checkout.selectPaymentType'));
        return;
      }

      await clearOldPending();
      setIsLoading(true);

      try {
        await ensureAddressDefaultThenProceed();

        const referral_reward_claim_id = selectedReferrals.length > 0 ? selectedReferrals[0].id : 0;
        const { endpoint, body } = buildSubscriptionCheckoutRequest({
          plan: plan as Record<string, unknown>,
          mode,
          customer_id: user.id,
          start_date_ymd: formatMomentForApiYmd(startDate),
          address_payload: addressPayload,
          day_off_array: dayOffArray,
          coupon_applied: couponApplied,
          coupon_code: couponCode,
          celebrity_id: Number((plan as any).celebrity_id ?? 0) || 0,
          selected_payment: selectedPayment,
          payment_type: paymentType,
          is_hesabe_gateway,
          points_discount: pointsDiscount,
          use_points: usePoints,
          referral_reward_claim_id,
          selected_addons: selectedAddons,
        });

        const response: any = await postSubscriptionCheckoutRequest({ endpoint, body });

        if (response.success) {
          await clearSelectedSubscription();
          if (mode === 'pay') {
            const {
              charge_url,
              customer_subscription_id: sub_id,
              payment_settled,
            } = await resolveSubscriptionCheckoutPaymentUrl({
              checkout_response: response,
              customer_id: user.id,
              payment_gateway: selectedPayment,
            });

            if (charge_url) {
              CheckoutGlobalService.setContext({
                type: 'pending',
                data: {
                  selectedDate: formatMomentForApiYmd(startDate),
                  couponCode,
                  discountAmount: couponDiscount,
                  couponApplied,
                },
              });
              navigation.navigate('PaymentWebView', {
                url: charge_url,
                subscriptionId: sub_id ?? undefined,
              });
            } else if (payment_settled || sub_id != null) {
              /**
               * Zero-cost / already-captured purchase: no WebView.
               * Leave checkout and land on dashboard (MainTabs).
               */
              CheckoutGlobalService.setContext(null);
              void invalidateOnCheckoutSuccess(query_client, user.id);
              navigation.reset({
                index: 0,
                routes: [{ name: 'MainTabs' as never }],
              });
              /** Backend still returns “complete your payment…” for free/captured — use local copy. */
              Alert.alert(t('common.success'), t('paymentWebView.paymentSuccessMessage'));
            } else {
              Alert.alert(t('common.error'), t('checkout.failedToProcessSubscription'));
            }
          } else {
            void invalidateOnCheckoutSuccess(query_client, user.id);
            navigation.reset({
              index: 0,
              routes: [{ name: 'MainTabs' as never }],
            });
            Alert.alert(t('common.success'), t('checkout.requestSentSuccess'));
          }
        } else if (
          response.message === 'Customer Default Address does not exist.' ||
          response.message === 'Customer Default Address does not exist'
        ) {
          if (addresses.length === 0) {
            Alert.alert(t('checkout.addressRequired'), t('checkout.addAddressMessage'), [
              { text: t('address.addAddress'), onPress: () => navigation.navigate('AddressForm', {}) },
            ]);
          } else {
            Alert.alert(t('checkout.addressRequired'), t('checkout.setDefaultAddressMessage'), [
              { text: t('common.cancel'), style: 'cancel' },
              { text: t('checkout.myAddresses'), onPress: () => navigation.navigate('AddressLists') },
              { text: t('checkout.addNew'), onPress: () => navigation.navigate('AddressForm', {}) },
            ]);
          }
        } else {
          Alert.alert(
            t('common.error'),
            response.message ||
              (mode === 'pay' ? t('checkout.failedToProcessSubscription') : t('checkout.failedToRequestSubscription')),
          );
        }
      } catch (error: any) {
        Alert.alert(t('common.error'), error?.message || t('checkout.anErrorOccurred'));
      } finally {
        setIsLoading(false);
      }
    },
    [
      agreedToTerms,
      requireDeliveryAddress,
      validateCheckoutStartDate,
      plan,
      user?.id,
      paymentMethods.length,
      selectedPayment,
      startDate,
      addressPayload,
      dayOffArray,
      couponApplied,
      couponCode,
      paymentType,
      is_hesabe_gateway,
      pointsDiscount,
      usePoints,
      selectedReferrals,
      selectedAddons,
      couponDiscount,
      addresses.length,
      activeDateRanges,
      checkout_date_rules,
      pendingPlan?.id,
      t,
      navigation,
      query_client,
    ],
  );

  const proceedToPayment = async () => {
    await submitSubscriptionCheckout('pay');
  };

  const requestForSubscription = async () => {
    await submitSubscriptionCheckout('request');
  };

  const askPaymentMethod = () => {
    if (!requireDeliveryAddress()) {
      return;
    }
    if (!can_pay_online) {
      void requestForSubscription();
      return;
    }

    showActionSheet({
      title: t('checkout.paymentMethod'),
      message: t('checkout.chooseHowToProceed'),
      cancelLabel: t('common.cancel'),
      options: [
        { label: t('checkout.payNow'), onSelect: () => void proceedToPayment() },
        { label: t('checkout.requestSubscription'), onSelect: () => void requestForSubscription() },
      ],
    });
  };

  const handleCheckout = () => {
    if (!agreedToTerms) {
      Alert.alert(t('common.error'), t('checkout.agreeToTermsRequired'));
      return;
    }
    if (addresses_loading) {
      return;
    }
    if (!requireDeliveryAddress()) {
      return;
    }

    if (payment_mode === 'manual' || !can_pay_online) {
      void requestForSubscription();
      return;
    }
    if (payment_mode === 'online') {
      void proceedToPayment();
      return;
    }
    askPaymentMethod();
  };

  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createCheckoutStyles(themeColors), [themeColors]);
  const weekday_labels = useMemo(() => getLocalizedWeekdaysShort(locale ?? 'en'), [locale]);
  const day_off_summary = useMemo(
    () => formatCheckoutDayOffSummary(dayOffArray, weekday_labels),
    [dayOffArray, weekday_labels],
  );
  const start_date_hint = useMemo(() => {
    const base = t('checkout.deliveryStartsOn');
    if (!day_off_summary) {
      return base;
    }
    return `${base} · ${t('checkout.dayOffSelection')}: ${day_off_summary}`;
  }, [t, day_off_summary]);

  const plan_image_uri = useMemo(() => {
    return (
      pickCmsPageImageUri(plan_record) ??
      resolveImageUri(String(plan_record.thumbnail ?? plan_record.attachment ?? plan_record.image ?? ''))
    );
  }, [plan_record]);

  const plan_hero_title = useMemo(
    () => String(plan_record.name ?? plan_record.title ?? t('checkout.title')),
    [plan_record, t],
  );
  /** Custom builder uses `number_of_*`; catalog plans use `total_*` / `*_count`. */
  const plan_meals_count =
    Number(
      plan_record.number_of_meals ?? plan_record.total_meals ?? plan_record.meal_count ?? 0,
    ) || 0;
  const plan_snacks_count =
    Number(
      plan_record.number_of_snacks ?? plan_record.total_snacks ?? plan_record.snack_count ?? 0,
    ) || 0;
  const custom_meal_config_rows = useMemo(() => {
    if (plan_record.isCustomAllowed !== true) {
      return undefined;
    }
    const meals = Array.isArray(plan_record.customized_meals) ? plan_record.customized_meals : [];
    if (meals.length === 0) {
      return undefined;
    }
    const meal_word = t('subscriptions.meal') || 'Meal';
    return meals.map((meal: any, index: number) => {
      const protein = meal?.protein != null && String(meal.protein) !== '' ? formatNumber(Number(meal.protein) || meal.protein) : '—';
      const carbs = meal?.carbs != null && String(meal.carbs) !== '' ? formatNumber(Number(meal.carbs) || meal.carbs) : '—';
      return {
        label: `${meal_word} ${formatNumber(index + 1)}`,
        value: `P ${protein} g · C ${carbs} g`,
      };
    });
  }, [plan_record, t]);
  const footer_scroll_pad = useMemo(() => checkoutFooterScrollPadding(insets.bottom), [insets.bottom]);
  const footer_bottom_pad = useMemo(() => Math.max(insets.bottom, appTheme.spacing.sm), [insets.bottom]);
  const footer_fade_colors = useMemo(
    () => [hexToRgba(themeColors.background, 0) as string, themeColors.surface as string],
    [themeColors.background, themeColors.surface],
  );
  const loyalty_icon_bg = useMemo(
    () => hexToRgba(themeColors.secondary, 0.12) as string,
    [themeColors.secondary],
  );
  const cta_disabled = !agreedToTerms || !can_complete_subscription_purchase;
  const toggle_agreed_to_terms = useCallback(() => setAgreedToTerms(prev => !prev), []);

  const scroll_coupon_into_view = useCallback(() => {
    const y = Math.max(0, coupon_section_y_ref.current - appTheme.spacing.lg);
    requestAnimationFrame(() => {
      scroll_ref.current?.scrollTo({ y, animated: true });
    });
  }, []);

  const addons_collapsed_summary =
    selectedAddons.length > 0
      ? t('checkout.addonsSelectedCount', { count: selectedAddons.length })
      : t('checkout.selectAddons');
  const coupon_collapsed_summary = couponApplied
    ? couponCode || t('checkout.couponApplied')
    : t('checkout.addCouponHint');

  return (
    <StackScreenShell colors={themeColors}>
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={0}>
      <ScreenHeader title={t('checkout.title')} onBack={on_back_exit} scrollY={scrollY} />

      <Animated.ScrollView
        ref={scroll_ref}
        {...SCROLL_VIEW_PROPS}
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: header_top + CHECKOUT_GAP_BELOW_HEADER,
            paddingBottom: footer_scroll_pad,
          },
        ]}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive">
        <CheckoutPlanHeroCard
          eyebrow={t('checkout.selectedPlan')}
          title={plan_hero_title}
          image_uri={plan_image_uri}
          meals_label={`${plan_meals_count} ${t('subscriptions.meals')}`}
          snacks_label={`${plan_snacks_count} ${t('subscriptions.snacks')}`}
          days_label={`${planDays} ${t('subscriptions.daysLabel')}`}
          price_label={t('checkout.totalAmount')}
          price_value={formatCurrency(planCost)}
          meal_config_rows={custom_meal_config_rows}
        />

        <CheckoutSectionBlock label={t('order.selectDeliveryAddress')}>
          <CheckoutStudioCard>
            <CheckoutNavRow
              icon="calendar"
              label={t('checkout.startDate')}
              value={startDate.format('MMMM DD, YYYY')}
              hint={start_date_hint}
              on_press={() => setShowDatePicker(true)}
            />
            <CheckoutInCardDivider />
            <CheckoutNavRow
              icon="map"
              label={t('checkout.deliveryAddress')}
              value={
                address_for_subscription
                  ? String(address_for_subscription.name || t('address.addressFallback'))
                  : t('checkout.selectAddress')
              }
              hint={
                address_for_subscription
                  ? formatAddressSummaryLine(address_for_subscription as Record<string, unknown>, t) || undefined
                  : undefined
              }
              on_press={() => (addresses.length > 0 ? setShowAddressPicker(true) : go_to_add_address())}
            />
          </CheckoutStudioCard>
        </CheckoutSectionBlock>

        {!has_saved_delivery_address && !addresses_loading && user?.id != null ? (
          <View style={styles.addressRequiredBanner}>
            <SvgIcon name="map" size={20} color={themeColors.error} />
            <Text style={styles.addressRequiredBannerText}>{t('checkout.addAddressBeforeSubscription')}</Text>
            <Clickable onPress={go_to_add_address} accessibilityLabel={t('address.addAddress')} accessibilityRole="button" style={styles.addressRequiredCta}>
              <Text style={styles.addressRequiredCtaLabel}>{t('address.addAddress')}</Text>
            </Clickable>
          </View>
        ) : null}

        {addons.length > 0 ? (
          <View style={styles.addonsSection}>
            <CheckoutStudioCard>
              <Clickable
                onPress={open_addons_popup}
                haptics
                accessibilityRole="button"
                accessibilityLabel={t('checkout.addons')}
                style={styles.addonsSelectRow}>
                <Text style={styles.addonsSelectLabel} numberOfLines={1}>
                  {t('checkout.addons')}
                </Text>
                <View style={styles.addonsSelectTrailing}>
                  <Text style={styles.addonsSelectSummary} numberOfLines={1}>
                    {addons_collapsed_summary}
                  </Text>
                  <SvgIcon name="chevron-down" size={18} color={themeColors.textLight} />
                </View>
              </Clickable>
              {selectedAddons.length > 0 ? (
                <>
                  <CheckoutInCardDivider />
                  <View style={styles.selectedAddonsList}>
                    {selectedAddons.map((addon: any) => {
                      const at_max = addon.selectedQty >= addon.max_quantity;
                      const line_total =
                        (parseFloat(addon.delivery_charge) || 0) * (parseInt(addon.selectedQty) || 0) * planDays;
                      return (
                        <View key={addon.id} style={styles.selectedAddonItem}>
                          {addon.attachment ? (
                            <Image source={{ uri: addon.attachment }} style={styles.selectedAddonImage} resizeMode="cover" onError={() => {}} />
                          ) : (
                            <View style={[styles.selectedAddonImage, styles.imagePlaceholder]}>
                              <SvgIcon name="menu" size={24} color={themeColors.textLight} />
                            </View>
                          )}
                          <View style={styles.selectedAddonInfo}>
                            <Text style={styles.selectedAddonName}>
                              {getApiText(addon as Record<string, unknown>, 'name', 'a_name') || addon.name || addon.a_name}
                            </Text>
                            <Text style={styles.selectedAddonPrice}>
                              {formatCurrency(isNaN(line_total) ? 0 : line_total)}
                            </Text>
                          </View>
                          <View style={styles.addonQtyControls}>
                            <Clickable2
                              style={styles.addonQtyBtn}
                              onPress={() => adjustAddonQty(addon.id, -1)}
                              accessibilityRole="button"
                              accessibilityLabel={t('menu.decreaseQuantity')}
                              haptics={false}>
                              <View style={styles.addonQtyBtnInner}>
                                <SvgIcon name="minus" size={16} color={themeColors.text} />
                              </View>
                            </Clickable2>
                            <Text style={styles.addonQtyText}>{addon.selectedQty}</Text>
                            <Clickable2
                              style={[styles.addonQtyBtn, at_max && styles.addonQtyBtnDisabled]}
                              onPress={() => adjustAddonQty(addon.id, 1)}
                              disabled={at_max}
                              accessibilityRole="button"
                              accessibilityLabel={t('menu.increaseQuantity')}
                              haptics={false}>
                              <View style={styles.addonQtyBtnInner}>
                                <SvgIcon name="plus" size={16} color={at_max ? themeColors.textLight : themeColors.text} />
                              </View>
                            </Clickable2>
                          </View>
                        </View>
                      );
                    })}
                  </View>
                </>
              ) : null}
            </CheckoutStudioCard>
          </View>
        ) : null}

        {show_payment_section ? (
          <CheckoutSectionBlock label={t('checkout.paymentMethod')}>
            <CheckoutStudioCard>
              {show_payment_gateway_choice
                ? paymentMethods.map((method, index) => {
                    const is_selected = selectedPayment === method.id;
                    const is_last = index === paymentMethods.length - 1;
                    return (
                      <Clickable
                        key={method.id}
                        onPress={() => setSelectedPayment(method.id)}
                        style={[styles.paymentRowWrap, !is_last && styles.paymentRowBorder]}>
                        <View style={[styles.paymentRow, is_selected && styles.paymentRowSelected]}>
                          <View style={[styles.radio, is_selected && styles.radioSelected]}>{is_selected ? <View style={styles.radioDot} /> : null}</View>
                          <SvgIcon name={method.icon} size={20} color={is_selected ? themeColors.primary : themeColors.textSecondary} />
                          <Text style={[styles.paymentName, is_selected && styles.paymentNameSelected]} numberOfLines={2}>
                            {method.displayName || method.name}
                          </Text>
                          {is_selected ? <SvgIcon name="check" size={18} color={themeColors.primary} /> : null}
                        </View>
                      </Clickable>
                    );
                  })
                : null}
              {show_hesabe_payment_type_picker ? (
                <View style={styles.hesabeTypesContainer}>
                  <Text style={styles.hesabeTypesTitle}>{t('checkout.selectPaymentType')}</Text>
                  <View style={styles.hesabeTypesRow}>
                    {HESABE_PAYMENT_TYPES.map(type => (
                      <Clickable key={type.id} style={[styles.hesabeTypeChip, paymentType === type.id && styles.hesabeTypeChipSelected]} onPress={() => setPaymentType(type.id)}>
                        <View style={styles.hesabeTypeChipInner}>
                          <Text style={[styles.hesabeTypeText, paymentType === type.id && styles.hesabeTypeTextSelected]}>{type.name}</Text>
                        </View>
                      </Clickable>
                    ))}
                  </View>
                </View>
              ) : null}
            </CheckoutStudioCard>
          </CheckoutSectionBlock>
        ) : null}

        {referrals.length > 0 ? (
          <CheckoutSectionBlock label={t('checkout.referralDiscount')}>
            <CheckoutStudioCard>
              {referrals.map((referral: any, referral_index: number) => {
                const isSelected = selectedReferrals.some(r => r.id === referral.id);
                const is_last_referral = referral_index === referrals.length - 1;
                return (
                  <Clickable
                    key={referral.id}
                    style={[styles.referralItemOuter, !is_last_referral && styles.referralItemBorder]}
                    onPress={() => toggleReferral(referral)}
                    disabled={total === 0}>
                    <View style={styles.referralItemRow}>
                      <View style={styles.referralInfo}>
                        <Text style={styles.referralValue}>
                          {referral.reward_value}{' '}
                          {!referralRewardIsPercentage(referral.reward_value) ? display_currency : ''} Discount
                        </Text>
                        <Text style={styles.referralFrom}>From: {referral.referrer_name || 'Friend'}</Text>
                      </View>
                      <View style={[styles.referralCheckbox, isSelected && styles.referralCheckboxSelected]}>
                        {isSelected ? <SvgIcon name="check" size={14} color={themeColors.onPrimary} /> : null}
                      </View>
                    </View>
                  </Clickable>
                );
              })}
            </CheckoutStudioCard>
          </CheckoutSectionBlock>
        ) : null}

        <View
          onLayout={e => {
            coupon_section_y_ref.current = e.nativeEvent.layout.y;
          }}>
          <CheckoutCollapsibleSection
            label={t('checkout.couponCode')}
            collapsed_summary={coupon_collapsed_summary}
            expanded={coupon_collapse.expanded}
            on_toggle={coupon_collapse.toggle}>
            <View style={styles.couponRow}>
              <View style={styles.couponInputWrap}>
                <Input
                  placeholder={t('checkout.enterCouponPlaceholder')}
                  value={couponCode}
                  onChangeText={setCouponCode}
                  autoCapitalize="characters"
                  editable={!couponApplied}
                  containerStyle={{ marginBottom: 0 }}
                  onFocus={scroll_coupon_into_view}
                />
              </View>
              <Clickable
                contentStyle={[styles.couponApplyBtn, couponApplied && styles.couponApplyBtnOutline]}
                onPress={
                  couponApplied
                    ? () => {
                        setCouponApplied(false);
                        setCouponDiscount(0);
                        setCouponCode('');
                      }
                    : applyCoupon
                }
                haptics
                accessibilityRole="button"
                accessibilityLabel={couponApplied ? t('order.remove') : t('checkout.applyCoupon')}>
                <Text style={[styles.couponApplyText, couponApplied && styles.couponApplyTextOutline]} numberOfLines={1}>
                  {couponApplied ? t('order.remove') : t('checkout.applyCoupon')}
                </Text>
              </Clickable>
            </View>
          </CheckoutCollapsibleSection>
        </View>

        {show_loyalty && loyaltyPoints > 0 ? (
          <CheckoutSectionBlock label={t('account.loyaltyRewards')}>
            <CheckoutStudioCard>
              <Clickable
                style={styles.loyaltyRowWrap}
                onPress={() => canRedeemPoints && setUsePoints(!usePoints)}
                disabled={!canRedeemPoints}>
                <View style={[styles.loyaltyCardRow, !canRedeemPoints && styles.loyaltyCardDisabled]}>
                  <View style={[styles.loyaltyIconWrap, { backgroundColor: loyalty_icon_bg }]}>
                    <SvgIcon name="star" size={20} color={canRedeemPoints ? themeColors.secondary : themeColors.textLight} />
                  </View>
                  <View style={styles.loyaltyInfo}>
                    <Text style={[styles.loyaltyTitle, !canRedeemPoints && styles.loyaltyTitleDisabled]}>{t('checkout.useLoyaltyPoints')}</Text>
                    <Text style={styles.loyaltySubtitle}>
                      {loyaltyPoints} points ({formatCurrency(pointsValue)})
                    </Text>
                    {!canRedeemPoints && minimumInvoiceToRedeem > 0 ? (
                      <Text style={styles.loyaltyMinimum}>
                        Min. order {formatCurrency(Number(minimumInvoiceToRedeem) || 0)} required
                      </Text>
                    ) : null}
                  </View>
                  <View style={[styles.checkbox, usePoints && styles.checkboxSelected, !canRedeemPoints && styles.checkboxDisabled]}>
                    {usePoints ? <SvgIcon name="check" size={16} color={themeColors.onPrimary} /> : null}
                  </View>
                </View>
              </Clickable>
            </CheckoutStudioCard>
          </CheckoutSectionBlock>
        ) : null}

        <CheckoutSectionBlock label={t('checkout.priceDetails')}>
          <CheckoutStudioCard>
            <View style={styles.summaryCardInner}>
              <CheckoutSummaryRow label={t('order.subtotal')} value={formatCurrency(isNaN(subtotal) ? 0 : subtotal)} />
              {addonsTotal > 0 ? (
                <CheckoutSummaryRow label={t('checkout.addons')} value={formatCurrency(isNaN(addonsTotal) ? 0 : addonsTotal)} />
              ) : null}
              {couponDiscount > 0 ? (
                <CheckoutSummaryRow
                  label={t('checkout.couponDiscount')}
                  value={`-${formatCurrency(couponDiscount)}`}
                  accent="discount"
                />
              ) : null}
              {referralDiscount > 0 ? (
                <CheckoutSummaryRow
                  label={t('checkout.referralDiscount')}
                  value={`-${formatCurrency(referralDiscount)}`}
                  accent="discount"
                />
              ) : null}
              {pointsDiscount > 0 ? (
                <CheckoutSummaryRow
                  label={t('checkout.pointsDiscount')}
                  value={`-${formatCurrency(pointsDiscount)}`}
                  accent="discount"
                />
              ) : null}
              <View style={[styles.priceDivider, { backgroundColor: themeColors.borderLight ?? themeColors.border }]} />
              <CheckoutSummaryRow
                label={t('order.total')}
                value={formatCurrency(isNaN(total) ? 0 : total)}
                accent="total"
              />
            </View>
          </CheckoutStudioCard>
        </CheckoutSectionBlock>

        <View style={styles.termsSection}>
          <CheckoutStudioCard>
            <View style={styles.termsInner}>
              <Clickable
                onPress={toggle_agreed_to_terms}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: agreedToTerms }}
                accessibilityLabel={t('auth.agreeToTerms')}
                hitSlop={appTheme.hitSlop.small}
                style={styles.checkboxHit}>
                <View style={[styles.checkbox, agreedToTerms && styles.checkboxSelected]}>
                  {agreedToTerms ? <SvgIcon name="check" size={14} color={themeColors.onPrimary} /> : null}
                </View>
              </Clickable>
              <Text style={styles.termsText}>
                {t('auth.agreeToTerms')}{' '}
                <Text
                  style={styles.termsLink}
                  onPress={() =>
                    navigation.navigate('TermsAndConditions', {
                      onAccept: () => setAgreedToTerms(true),
                    })
                  }
                  accessibilityRole="link">
                  {t('auth.termsAndConditions')}
                </Text>
              </Text>
            </View>
          </CheckoutStudioCard>
        </View>
      </Animated.ScrollView>

      <View style={[styles.footer, { paddingBottom: footer_bottom_pad }]}>
        <LinearGradient colors={footer_fade_colors} style={styles.footerFade} pointerEvents="none" />
        <View style={styles.footerTotalRow}>
          <Text style={styles.footerTotalLabel}>{t('order.total')}</Text>
          <Text style={styles.footerTotalValue}>
            {formatCurrency(isNaN(total) ? 0 : total)}
          </Text>
        </View>
        <SolidCtaClickable
          label={checkout_cta_is_request ? t('checkout.requestSubscription') : t('checkout.placeOrder')}
          onPress={handleCheckout}
          disabled={cta_disabled}
          variant="primary"
          haptics={true}
          accessibilityLabel={
            checkout_cta_is_request ? t('checkout.requestSubscription') : t('checkout.placeOrder')
          }
        />
      </View>

      <CheckoutStartDateCalendarSheet
        visible={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        selected_date={startDate}
        selected_day_offs={dayOffArray}
        temp_day_off_options={tempDayOffArray}
        forced_day_offs={forcedDayOff}
        max_day_off={maxDayOff}
        checkout_date_rules={checkout_date_rules_base}
        is_date_blocked={isDateBlockedByActiveSubscription}
        min_month={min_checkout_start_date.clone().startOf('month')}
        max_month={max_checkout_month}
        on_save={(date, next_day_offs) => {
          const next = date.clone().startOf('day');
          setStartDate(next);
          setDayOffArray(next_day_offs);
        }}
      />

      <CheckoutAddressPickerSheet
        visible={showAddressPicker}
        onClose={() => setShowAddressPicker(false)}
        addresses={addresses}
        selected_address_id={selectedAddress?.id ?? address_for_subscription?.id}
        on_select={address => {
          setSelectedAddress(address);
          setShowAddressPicker(false);
        }}
        on_add_new={() => {
          setShowAddressPicker(false);
          navigation.navigate('AddressForm', {});
        }}
      />

      <BottomSheetPopup visible={showAddonsPopup} onClose={() => setShowAddonsPopup(false)} title={t('checkout.selectAddons')} animationType="slide" showDragHandle sheetStyle={styles.modalContainer}>
        <ScrollView style={styles.addonsList} keyboardShouldPersistTaps="handled">
          {addons.map((addon: any) => (
            <View key={addon.id} style={styles.addonCard}>
              {addon.attachment ? (
                <Image source={{ uri: addon.attachment }} style={styles.addonImage} resizeMode="cover" onError={() => {}} />
              ) : (
                <View style={[styles.addonImage, styles.imagePlaceholder]}>
                  <SvgIcon name="menu" size={28} color={themeColors.textLight} />
                </View>
              )}
              <View style={styles.addonInfo}>
                <Text style={styles.addonName}>{getApiText(addon as Record<string, unknown>, 'name', 'a_name') || addon.name || addon.a_name}</Text>
                <Text style={styles.addonPrice}>
                  {formatCurrency(parseFloat(addon.delivery_charge) || 0)} / day
                </Text>
                <Text style={styles.addonMaxQty}>Max: {addon.max_quantity}</Text>
              </View>
              <View style={styles.addonQtyControls}>
                <Clickable2
                  style={[styles.addonQtyBtn, addon.selectedQty === 0 && styles.addonQtyBtnDisabled]}
                  onPress={() => adjustAddonQty(addon.id, -1)}
                  disabled={addon.selectedQty === 0}
                  accessibilityRole="button"
                  accessibilityLabel={t('menu.decreaseQuantity')}
                  haptics={false}>
                  <View style={styles.addonQtyBtnInner}>
                    <SvgIcon
                      name="minus"
                      size={16}
                      color={addon.selectedQty === 0 ? themeColors.textLight : themeColors.text}
                    />
                  </View>
                </Clickable2>
                <Text style={styles.addonQtyText}>{addon.selectedQty}</Text>
                <Clickable2
                  style={[styles.addonQtyBtn, addon.selectedQty >= addon.max_quantity && styles.addonQtyBtnDisabled]}
                  onPress={() => adjustAddonQty(addon.id, 1)}
                  disabled={addon.selectedQty >= addon.max_quantity}
                  accessibilityRole="button"
                  accessibilityLabel={t('menu.increaseQuantity')}
                  haptics={false}>
                  <View style={styles.addonQtyBtnInner}>
                    <SvgIcon
                      name="plus"
                      size={16}
                      color={addon.selectedQty >= addon.max_quantity ? themeColors.textLight : themeColors.text}
                    />
                  </View>
                </Clickable2>
              </View>
            </View>
          ))}
        </ScrollView>
        <Clickable style={styles.addonsDoneBtn} onPress={() => setShowAddonsPopup(false)}>
          <View style={styles.addonsDoneBtnInner}>
            <Text style={styles.addonsDoneBtnText}>{t('common.done')}</Text>
          </View>
        </Clickable>
      </BottomSheetPopup>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <MainLoader size={48} color={themeColors.primary} />
        </View>
      )}
    </KeyboardAvoidingView>
    </StackScreenShell>
  );
};

function createCheckoutStyles(c: ThemeColors) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: 'transparent',
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
    },
    content: {
      flex: 1,
    },
    addressRequiredBanner: {
      marginTop: appTheme.spacing.md,
      padding: DASH_STUDIO_INNER,
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: hexToRgba(c.error, 0.08) as string,
      flexDirection: 'column',
      gap: appTheme.spacing.sm,
      ...androidFlatSurfaceStyle(),
    },
    addressRequiredBannerText: {
      fontSize: androidScaleFont(14),
      lineHeight: androidScaleFont(20),
      color: c.text,
      fontWeight: '500',
    },
    addressRequiredCta: {
      alignSelf: 'flex-start',
      paddingVertical: appTheme.spacing.xs,
    },
    addressRequiredCtaLabel: {
      fontSize: androidScaleFont(15),
      fontWeight: '700',
      color: c.primary,
    },
    dayOffCardInner: {
      padding: DASH_STUDIO_INNER,
      gap: appTheme.spacing.sm,
    },
    dayOffRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    addonsSection: {
      marginTop: appTheme.spacing.md,
    },
    addonsSelectRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: appTheme.spacing.sm,
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: androidScaleSize(14),
      minWidth: 0,
    },
    addonsSelectLabel: {
      flexShrink: 1,
      fontSize: androidScaleFont(15),
      fontWeight: '800',
      letterSpacing: -0.2,
      color: c.text,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    addonsSelectTrailing: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexShrink: 1,
      minWidth: 0,
    },
    addonsSelectSummary: {
      flexShrink: 1,
      fontSize: androidScaleFont(13),
      fontWeight: '600',
      color: c.textSecondary,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    paymentRowWrap: {
      paddingHorizontal: DASH_STUDIO_INNER,
    },
    paymentRowBorder: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderLight ?? c.border,
    },
    paymentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.lg,
    },
    paymentRowSelected: {
      backgroundColor: hexToRgba(c.primary, 0.06) as string,
    },
    referralItemBorder: {
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: c.borderLight ?? c.border,
    },
    loyaltyRowWrap: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.md,
    },
    loyaltyIconWrap: {
      width: androidScaleSize(44),
      height: androidScaleSize(44),
      borderRadius: 14,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    summaryCardInner: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.sm,
    },
    card: {
      backgroundColor: c.surface,
      marginHorizontal: appTheme.spacing.md,
      padding: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      flexDirection: 'row',
      alignItems: 'center',
    },
    planInfo: {
      flex: 1,
    },
    planName: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
      marginBottom: appTheme.spacing.xs,
    },
    planDetails: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    planPrice: {
      ...appTheme.typography.h3,
      color: c.primary,
      fontWeight: '700',
    },
    dateText: {
      ...appTheme.typography.body,
      color: c.text,
      flex: 1,
      marginStart: appTheme.spacing.md,
    },
    addressInfo: {
      flex: 1,
      marginStart: appTheme.spacing.md,
    },
    addressTitle: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '500',
      marginBottom: appTheme.spacing.xs,
    },
    addressDetails: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    paymentMethodsContainer: {
      gap: 10,
    },
    paymentCardOuter: {
      backgroundColor: c.surface,
      padding: appTheme.spacing.md,
      borderRadius: 15,
      alignSelf: 'stretch',
      borderWidth: 2,
      borderColor: c.border,
      ...appTheme.shadows.sm,
      shadowColor: c.shadowColor,
    },
    paymentCardRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
    },
    paymentCardSelected: {
      borderColor: c.primary,
      backgroundColor: c.primary + '08',
    },
    paymentNameSelected: {
      color: c.primary,
      fontWeight: '600',
    },
    paymentLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    radio: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: c.border,
      marginRight: appTheme.spacing.md,
      justifyContent: 'center',
      alignItems: 'center',
    },
    radioSelected: {
      borderColor: c.primary,
    },
    radioDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: c.primary,
    },
    paymentName: {
      ...appTheme.typography.body,
      color: c.text,
      flex: 1,
      minWidth: 0,
    },
    couponRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.md,
    },
    couponInputWrap: {
      flex: 1,
      minWidth: 0,
    },
    couponApplyBtn: {
      flexShrink: 0,
      alignSelf: 'center',
      minHeight: androidScaleSize(40),
      paddingHorizontal: appTheme.spacing.md,
      paddingVertical: androidScaleSize(10),
      borderRadius: CHECKOUT_CHIP_RADIUS,
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
      ...androidResolveShadow(appTheme.shadows.xs),
    },
    couponApplyBtnOutline: {
      backgroundColor: c.surface,
      borderWidth: StyleSheet.hairlineWidth * 2,
      borderColor: c.primary,
    },
    couponApplyText: {
      fontSize: androidScaleFont(13),
      fontWeight: '800',
      color: c.onPrimary,
      letterSpacing: -0.1,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    couponApplyTextOutline: {
      color: c.primary,
    },
    loyaltyCardOuter: {
      backgroundColor: c.surface,
      padding: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      alignSelf: 'stretch',
    },
    loyaltyCardRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      gap: appTheme.spacing.md,
    },
    loyaltyInfo: {
      flex: 1,
      minWidth: 0,
    },
    loyaltyTitle: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '500',
      marginBottom: appTheme.spacing.xs,
    },
    loyaltySubtitle: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    loyaltyCardDisabled: {
      opacity: 0.6,
    },
    loyaltyTitleDisabled: {
      color: c.textLight,
    },
    loyaltyMinimum: {
      fontSize: 11,
      color: c.warning,
      marginTop: 4,
    },
    checkbox: {
      width: 22,
      height: 22,
      borderRadius: CHECKOUT_CHIP_RADIUS - 4,
      borderWidth: 2,
      borderColor: c.border,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    checkboxSelected: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    dayOffChipTextSelected: {
      color: c.onPrimary,
    },
    dayOffInfo: {
      marginTop: 8,
    },
    dayOffInfoText: {
      fontSize: 12,
      color: c.textSecondary,
    },
    planImageContainer: {
      marginHorizontal: appTheme.spacing.md,
      marginBottom: appTheme.spacing.md,
      borderRadius: 15,
      overflow: 'hidden',
      ...appTheme.shadows.md,
      shadowColor: c.shadowColor,
    },
    planImage: {
      width: '100%',
      height: 200,
      backgroundColor: c.background,
    },
    planImagePlaceholder: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    planSummaryCard: {
      backgroundColor: c.surface,
      marginHorizontal: appTheme.spacing.md,
      borderRadius: 15,
      padding: appTheme.spacing.md,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      ...appTheme.shadows.sm,
      shadowColor: c.shadowColor,
    },
    priceBreakdownCard: {
      ...studioCardShell(c),
      backgroundColor: c.backgroundElevated,
      padding: appTheme.spacing.md,
    },
    priceRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
    },
    priceLabel: {
      fontSize: 14,
      color: c.textSecondary,
      flex: 1,
    },
    priceValue: {
      fontSize: 15,
      fontWeight: '600',
      color: c.text,
      textAlign: 'right',
    },
    discountText: {
      color: c.success,
    },
    priceDivider: {
      height: 1,
      backgroundColor: c.border,
      marginVertical: 12,
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: 8,
    },
    totalLabel: {
      fontSize: 17,
      fontWeight: '700',
      color: c.text,
      flex: 1,
    },
    totalValue: {
      fontSize: 20,
      fontWeight: '700',
      color: c.primary,
      textAlign: 'right',
    },
    planCard: {
      ...studioCardShell(c),
      backgroundColor: c.backgroundElevated,
      marginTop: 0,
      overflow: 'hidden',
      alignSelf: 'stretch',
    },
    planCardImageWrapper: {
      position: 'relative',
      width: '100%',
      height: 160,
    },
    planCardImage: {
      width: '100%',
      height: '100%',
      backgroundColor: c.background,
    },
    planCardImagePlaceholder: {
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: c.primary + '10',
    },
    planCardOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.1)',
    },
    planCardContent: {
      padding: appTheme.spacing.lg,
    },
    planCardBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      backgroundColor: c.success + '15',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 20,
      gap: 6,
      marginBottom: appTheme.spacing.md,
    },
    planCardBadgeText: {
      fontSize: 12,
      fontWeight: '600',
      color: c.success,
    },
    planCardTitle: {
      fontSize: 20,
      fontWeight: '700',
      color: c.text,
      marginBottom: appTheme.spacing.md,
      letterSpacing: 0.3,
    },
    planCardDetailsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 16,
      marginBottom: appTheme.spacing.lg,
    },
    planCardDetailItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    planCardDetailText: {
      fontSize: 13,
      fontWeight: '600',
      color: c.text,
    },
    planCardPriceRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: appTheme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: c.border,
    },
    planCardPriceLabel: {
      fontSize: 14,
      color: c.textSecondary,
      fontWeight: '500',
    },
    planCardPrice: {
      fontSize: 24,
      fontWeight: '700',
      color: c.primary,
    },
    dayOffChipInner: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    dayOffChip: {
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: c.surfaceLight ?? c.surfaceFade ?? c.surface,
    },
    dayOffChipSelected: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    dayOffChipForced: {
      backgroundColor: c.textLight,
      borderColor: c.textLight,
    },
    dayOffChipText: {
      fontSize: 13,
      fontWeight: '500',
      color: c.text,
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: appTheme.spacing.md,
      width: '100%',
    },
    addonsAddHit: {
      justifyContent: 'center',
    },
    addonsAddInner: {
      paddingVertical: appTheme.spacing.xs,
      paddingHorizontal: appTheme.spacing.xs,
    },
    addonsButton: {
      fontSize: 14,
      fontWeight: '600',
      color: c.primary,
    },
    selectedAddonsList: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: DASH_STUDIO_INNER,
      alignSelf: 'stretch',
    },
    selectedAddonItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    selectedAddonImage: {
      width: 50,
      height: 50,
      borderRadius: 8,
      marginRight: appTheme.spacing.md,
    },
    imagePlaceholder: {
      backgroundColor: c.background,
      justifyContent: 'center',
      alignItems: 'center',
    },
    selectedAddonInfo: {
      flex: 1,
      flexDirection: 'column',
    },
    selectedAddonName: {
      fontSize: 14,
      color: c.text,
    },
    selectedAddonPrice: {
      fontSize: 12,
      fontWeight: '600',
      color: c.primary,
      marginTop: 2,
    },
    hesabeTypesContainer: {
      marginTop: appTheme.spacing.xs,
      marginHorizontal: DASH_STUDIO_INNER,
      marginBottom: DASH_STUDIO_INNER,
      padding: appTheme.spacing.md,
      backgroundColor: hexToRgba(c.text, 0.04) as string,
      borderRadius: CHECKOUT_CHIP_RADIUS,
      alignSelf: 'stretch',
    },
    hesabeTypeChipInner: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 2,
    },
    hesabeTypesTitle: {
      fontSize: androidScaleFont(13),
      fontWeight: '700',
      color: c.textSecondary,
      marginBottom: appTheme.spacing.sm,
      letterSpacing: 0.2,
    },
    hesabeTypesRow: {
      flexDirection: 'row',
      gap: 8,
    },
    hesabeTypeChip: {
      flex: 1,
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderRadius: CHECKOUT_CHIP_RADIUS,
      backgroundColor: c.surface,
      alignItems: 'center',
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: hexToRgba(c.text, 0.1) as string,
    },
    hesabeTypeChipSelected: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    hesabeTypeText: {
      fontSize: androidScaleFont(12),
      fontWeight: '600',
      color: c.text,
      textAlign: 'center',
    },
    hesabeTypeTextSelected: {
      color: c.onPrimary,
    },
    tabbyPromoContainer: {
      marginTop: appTheme.spacing.sm,
      alignSelf: 'stretch',
    },
    referralCard: {
      backgroundColor: c.surface,
      borderRadius: 12,
      padding: appTheme.spacing.md,
      alignSelf: 'stretch',
    },
    referralItemOuter: {
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.md,
      alignSelf: 'stretch',
    },
    referralItemOuterLast: {
      borderBottomWidth: 0,
    },
    referralItemRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    referralInfo: {
      flex: 1,
      minWidth: 0,
      marginRight: appTheme.spacing.sm,
    },
    referralValue: {
      fontSize: 14,
      fontWeight: '600',
      color: c.text,
    },
    referralFrom: {
      fontSize: 12,
      color: c.textSecondary,
      marginTop: 2,
    },
    referralCheckbox: {
      width: 22,
      height: 22,
      borderRadius: CHECKOUT_CHIP_RADIUS - 4,
      borderWidth: 2,
      borderColor: c.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    referralCheckboxSelected: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    checkboxDisabled: {
      opacity: 0.4,
    },
    termsSection: {
      marginTop: appTheme.spacing.md,
      marginBottom: 0,
    },
    termsInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.md,
      paddingHorizontal: DASH_STUDIO_INNER,
      paddingVertical: appTheme.spacing.md,
    },
    checkboxHit: {
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    termsTextBlock: {
      flex: 1,
      minWidth: 0,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    termsText: {
      flex: 1,
      minWidth: 0,
      fontSize: androidScaleFont(14),
      lineHeight: androidScaleSize(20),
      color: c.textSecondary,
      fontWeight: '500',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    termsLink: {
      fontSize: androidScaleFont(14),
      lineHeight: androidScaleSize(20),
      color: c.primary,
      fontWeight: '700',
      textDecorationLine: 'underline',
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    footer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: CHECKOUT_FOOTER_VERTICAL_PAD,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: hexToRgba(c.text, 0.14) as string,
      backgroundColor: c.surface,
      gap: appTheme.spacing.xs,
      ...androidResolveShadow({
        ...appTheme.shadows.sm,
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.08,
      }),
    },
    footerFade: {
      position: 'absolute',
      top: -CHECKOUT_FOOTER_VERTICAL_PAD,
      left: 0,
      right: 0,
      height: CHECKOUT_FOOTER_VERTICAL_PAD,
    },
    footerTotalRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: appTheme.spacing.md,
    },
    footerTotalLabel: {
      fontSize: androidScaleFont(12),
      fontWeight: '700',
      color: c.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.55,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    footerTotalValue: {
      fontSize: androidScaleFont(22),
      fontWeight: '800',
      color: c.primary,
      letterSpacing: -0.4,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : {}),
    },
    modalContainer: {
      backgroundColor: c.surface,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: '80%',
    },
    calendarModalContainer: {
      backgroundColor: c.surface,
      borderTopLeftRadius: appTheme.borderRadius.xl,
      borderTopRightRadius: appTheme.borderRadius.xl,
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.md,
      ...appTheme.shadows.modal,
      shadowColor: c.shadowColor,
    },
    calendarSheetInner: {
      width: '100%',
      paddingTop: appTheme.spacing.xs,
    },
    calendarNavRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: appTheme.spacing.sm,
      paddingHorizontal: appTheme.spacing.xs,
    },
    calendarNavBtn: {
      minWidth: 44,
      minHeight: 44,
      justifyContent: 'center',
      alignItems: 'center',
    },
    calendarNavBtnDisabled: {
      opacity: 0.4,
    },
    calendarMonthTitle: {
      ...appTheme.typography.bodyMedium,
      color: c.text,
      flex: 1,
      textAlign: 'center',
    },
    calendarWeekdaysRow: {
      flexDirection: 'row',
      width: '100%',
      paddingBottom: appTheme.spacing.xs,
      ...horizontalLtrDirectionStyle,
    },
    calendarWeekdayCell: {
      flex: 1,
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    calendarWeekdayText: {
      width: '100%',
      textAlign: 'center',
      ...appTheme.typography.caption,
      color: c.textSecondary,
      fontWeight: '600',
    },
    calendarGrid: {
      width: '100%',
    },
    calendarWeekRow: {
      flexDirection: 'row',
      width: '100%',
      alignItems: 'stretch',
      ...horizontalLtrDirectionStyle,
    },
    calendarDayCellOuter: {
      flex: 1,
      minWidth: 0,
      aspectRatio: 1,
      maxHeight: 52,
      padding: 2,
    },
    calendarDayCellInner: {
      flex: 1,
      width: '100%',
      height: '100%',
      minHeight: 0,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: appTheme.borderRadius.pill,
      overflow: 'hidden',
    },
    calendarDayCellSelected: {
      backgroundColor: c.primary,
    },
    calendarDayCellDisabled: {
      opacity: 0.4,
    },
    calendarDayCellBlocked: {
      backgroundColor: c.background,
    },
    calendarDayText: {
      ...appTheme.typography.bodySmall,
      textAlign: 'center',
      width: '100%',
      color: c.text,
    },
    calendarDayTextSelected: {
      color: c.onPrimary ?? c.white,
      fontWeight: '600',
    },
    calendarDayTextDisabled: {
      color: c.textLight,
    },
    calendarDayBlockedDot: {
      position: 'absolute',
      bottom: 5,
      alignSelf: 'center',
      width: 4,
      height: 4,
      borderRadius: 2,
      backgroundColor: c.textLight,
    },
    calendarActionRow: {
      flexDirection: 'row',
      width: '100%',
      gap: appTheme.spacing.md,
      marginTop: appTheme.modal.footerMarginTop,
      paddingTop: appTheme.modal.footerPaddingTop,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.border,
    },
    calendarActionSecondary: {
      flex: 1,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.surfaceLight ?? c.surfaceFade ?? c.surface,
    },
    calendarActionSecondaryText: {
      ...pillButtonLabelCentered,
      ...appTheme.typography.body,
      fontWeight: '600',
      color: c.text,
    },
    calendarActionPrimary: {
      flex: 1,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.primary,
    },
    calendarActionPrimaryText: {
      ...pillButtonLabelCentered,
      ...appTheme.typography.body,
      fontWeight: '700',
      color: c.onPrimary ?? c.white,
    },
    addonsList: {
      padding: 16,
    },
    addonCard: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 16,
      borderRadius: 12,
      marginBottom: 12,
      backgroundColor: c.background,
    },
    addonImage: {
      width: 60,
      height: 60,
      borderRadius: 8,
      marginRight: 12,
    },
    addonInfo: {
      flex: 1,
    },
    addonName: {
      fontSize: 15,
      fontWeight: '600',
      color: c.text,
      marginBottom: 4,
    },
    addonPrice: {
      fontSize: 13,
      color: c.primary,
      marginBottom: 2,
    },
    addonMaxQty: {
      fontSize: 11,
      color: c.textSecondary,
    },
    addonQtyControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
    },
    addonQtyBtn: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: c.surfaceLight ?? c.surfaceFade ?? c.surface,
      overflow: 'hidden',
    },
    addonQtyBtnInner: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    addonQtyBtnDisabled: {
      opacity: 0.4,
    },
    addonQtyText: {
      fontSize: 16,
      fontWeight: '600',
      color: c.text,
      minWidth: 24,
      textAlign: 'center',
      lineHeight: 20,
      ...(Platform.OS === 'android' ? { includeFontPadding: false } : null),
    },
    addonsDoneBtn: {
      backgroundColor: c.primary,
      margin: 16,
      padding: 16,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'stretch',
    },
    addonsDoneBtnInner: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    addonsDoneBtnText: {
      ...pillButtonLabelCentered,
      fontSize: 16,
      fontWeight: '600',
      color: c.onPrimary,
    },
    loadingOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.backdrop,
      justifyContent: 'center',
      alignItems: 'center',
    },
  });
}
